import {
  appendUtmParams,
  type ProposalShareLinkMedium,
  useAuthenticatedUser,
  useProposalShareLinkRequest,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  EmptyState,
  LoadingButton,
} from '@airship-platform/jam-web/components';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { useAppointmentProposals } from '../../../data-access/appointmentProposals';
import { useAppointments } from '../../../data-access/appointments';
import { useProposalEngagement } from '../../../data-access/proposalEngagement';
import SectionWrapper from '../../../patterns/components/SectionWrapper';
import {
  emailContactsOf,
  mobilePhonesOf,
} from './follow-up-section/followUpMessage.utils';
import { type FollowUpChannel } from './follow-up-section/FollowUpNativeActions';
import FollowUpSection, {
  FollowUpSectionInputs,
} from './sections/FollowUpSection';
import ProposalEngagementSummary from './sections/ProposalEngagementSummary';

const FORM_ID = 'proposalFollowUpForm';

const UTM_MEDIUM: Record<FollowUpChannel, ProposalShareLinkMedium> = {
  text: 'sms',
  email: 'email',
  share: 'share',
  copy: 'copy',
};

const FollowUpPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuthenticatedUser();
  const { currentAppointment } = useAppointments();
  const { currentProposal, isLoading, sendProposalFollowUp } =
    useAppointmentProposals();
  const { fetchProposalEngagements } = useProposalEngagement();
  const { fetchAppointmentProposalOptions } = useAppointmentProposalOptions();
  const requestShareLink = useProposalShareLinkRequest();

  const shareLinkRef = useRef<Promise<{ url: string }> | null>(null);
  const [isLoadingChannel, setIsLoadingChannel] = useState<FollowUpChannel>();

  const appointmentId = currentProposal?.appointmentId;
  const appointmentProposalId = currentProposal?.appointmentProposalId;

  useEffect(() => {
    if (appointmentId && appointmentProposalId) {
      fetchProposalEngagements([{ appointmentId, appointmentProposalId }]);
    }
  }, [appointmentId, appointmentProposalId, fetchProposalEngagements]);

  useEffect(() => {
    if (appointmentId && appointmentProposalId) {
      fetchAppointmentProposalOptions(appointmentId, appointmentProposalId);
    }
  }, [appointmentId, appointmentProposalId, fetchAppointmentProposalOptions]);

  if (!currentProposal || !currentAppointment) {
    return (
      <EmptyState
        title="Proposal not found"
        subtitle="Oops! The requested proposal was not found."
      />
    );
  }

  const contacts = currentAppointment.customer.contacts ?? [];

  const getShareLink = async (channel: FollowUpChannel) => {
    setIsLoadingChannel(channel);

    try {
      shareLinkRef.current ??= requestShareLink(
        currentProposal.appointmentId,
        currentProposal.appointmentProposalId,
        { name: currentAppointment.customer.name }
      );

      const { url } = await shareLinkRef.current;

      return appendUtmParams(url, {
        medium: UTM_MEDIUM[channel],
        campaign: 'followup',
      });
    } catch {
      shareLinkRef.current = null;
      showToast('We were unable to prepare the proposal link', 'error');
      return null;
    } finally {
      setIsLoadingChannel(undefined);
    }
  };

  const defaultValues: FollowUpSectionInputs = {
    emailFilter: '',
    emails: emailContactsOf(contacts).slice(0, 1),
    phoneFilter: '',
    smsPhones: mobilePhonesOf(contacts)
      .slice(0, 1)
      .map((phone) => phone.value),
    followUpMessage: `Hi ${currentAppointment.customer?.name},

I wanted to follow up on the proposal I sent over. Based on the needs we discussed, we put together a great option for your home; one that balances comfort, efficiency, and long-term value.

If you have any questions or want to go over anything together, I am just a call or email away. Let's get you on the schedule.`,
  };

  const onSubmit = (data: FollowUpSectionInputs) =>
    sendProposalFollowUp(
      currentProposal.appointmentId,
      currentProposal.appointmentProposalId,
      {
        followUpMessage: data.followUpMessage,
        customerEmails: data.emails,
      }
    ).then(() => {
      showToast('Follow up message sent', 'success');
      navigate(-1);
    });

  return (
    <SectionWrapper
      maxWidth={'48rem'}
      width={'100%'}
      header="Follow up"
      subheader="Send a follow up message to the customer."
      actionsButtons={{
        left: {
          color: 'softGold',
          component: Link,
          to: '../',
          label: 'Cancel',
        },
        right: {
          type: 'submit',
          component: LoadingButton,
          form: FORM_ID,
          label: 'Send',
          loading: isLoading,
          disabled: isLoading,
        },
      }}
    >
      <ProposalEngagementSummary
        appointmentProposalId={currentProposal.appointmentProposalId}
      />

      <FollowUpSection
        formId={FORM_ID}
        defaultValues={defaultValues}
        contacts={contacts}
        isLoadingChannel={isLoadingChannel}
        getShareLink={getShareLink}
        isLoading={isLoading}
        onSubmit={onSubmit}
        engagementContext={
          user
            ? {
                organizationId: user.organizationId,
                appointmentId: currentProposal.appointmentId,
                appointmentProposalId: currentProposal.appointmentProposalId,
              }
            : undefined
        }
      />
    </SectionWrapper>
  );
};

export default FollowUpPage;
